import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './index.css';
import breadcrumbArrow from '@/assets/imgs/recruit/breadcrumb-arrow.svg';
import backArrow from '@/assets/imgs/recruit/detail-back-arrow.svg';
import relatedArrow from '@/assets/imgs/recruit/related-arrow.svg';
import { ContactSection } from '@/pages/Home/components/HomeBusiness';
import {
  recruitmentDetail,
  recruitmentPage,
  type RecruitmentRecord,
} from '@/service/api/recruitment';
import { RecruitHero } from '../components/RecruitHero';

const RELATED_RECRUITMENT_COUNT = 3;
const targetTypeTranslationKeys: Record<string, string> = {
  full_time: 'recruit.list.targetType.fullTime',
  intern: 'recruit.list.targetType.intern',
};

function decodeHtmlEntities(value: string) {
  if (typeof document === 'undefined' || !value.includes('&')) return value;

  let decoded = value;
  for (let index = 0; index < 2; index += 1) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    const nextValue = textarea.value;
    if (nextValue === decoded) break;
    decoded = nextValue;
  }
  return decoded;
}

function sanitizeRecruitmentHtml(value?: string) {
  const source = decodeHtmlEntities(value?.trim() ?? '');
  if (!source || typeof window === 'undefined') return source;

  const isHtml = /<\/?[a-z][\s\S]*>/i.test(source);
  const documentNode = new DOMParser().parseFromString(
    isHtml ? source : source.replace(/\r?\n/g, '<br />'),
    'text/html',
  );

  documentNode.querySelectorAll('script, iframe, object, embed').forEach(node => node.remove());
  documentNode.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attribute => {
      const name = attribute.name.toLowerCase();
      const attributeValue = attribute.value.trim().toLowerCase();
      if (
        name === 'style'
        || name.startsWith('on')
        || ((name === 'href' || name === 'src') && attributeValue.startsWith('javascript:'))
      ) {
        node.removeAttribute(attribute.name);
      }
    });
  });

  return documentNode.body.innerHTML;
}

function getRecruitmentPreview(record: RecruitmentRecord) {
  const source = record.dutySummary?.trim() || record.duty?.trim() || record.requirement?.trim() || '';
  if (!source) return '';

  const decoded = decodeHtmlEntities(source)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n');
  if (typeof window === 'undefined') return decoded.replace(/<[^>]*>/g, '');

  const documentNode = new DOMParser().parseFromString(decoded, 'text/html');
  return (documentNode.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

function getTargetTypeLabel(record: RecruitmentRecord, t: (key: string) => string) {
  const targetType = record.targetType?.trim() || '';
  return targetTypeTranslationKeys[targetType]
    ? t(targetTypeTranslationKeys[targetType])
    : targetType;
}

function RelatedRecruitmentCard({ record }: { record: RecruitmentRecord }) {
  const { t } = useTranslation();
  const preview = getRecruitmentPreview(record);
  const targetTypeLabel = getTargetTypeLabel(record, t);

  return (
    <article className="recruit-detail-related-card">
      <Link to={`/recruit-detail?id=${record.id ?? ''}`} className="recruit-detail-related-card__link">
        <span className="recruit-detail-related-card__title">
          {record.title || t('recruit.list.untitled')}
        </span>
        <img src={relatedArrow} alt="" />
      </Link>
      {targetTypeLabel && (
        <span className="recruit-detail-related-card__type">{targetTypeLabel}</span>
      )}
      {preview && <p>{preview}</p>}
    </article>
  );
}

function RecruitDetailSkeleton() {
  return (
    <div className="recruit-detail-layout recruit-detail-skeleton" aria-hidden="true">
      <div className="recruit-detail-skeleton__main">
        <div className="recruit-detail-skeleton__title" />
        <div className="recruit-detail-skeleton__line" />
        <div className="recruit-detail-skeleton__line" />
        <div className="recruit-detail-skeleton__panel" />
      </div>
      <div className="recruit-detail-skeleton__aside" />
    </div>
  );
}

const RecruitDetail = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const recruitmentId = searchParams.get('id')?.trim() ?? '';
  const [record, setRecord] = useState<RecruitmentRecord | null>(null);
  const [relatedRecruitments, setRelatedRecruitments] = useState<RecruitmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let ignore = false;
    const numericId = Number(recruitmentId);

    if (!recruitmentId || !Number.isFinite(numericId) || numericId <= 0) {
      setRecord(null);
      setRelatedRecruitments([]);
      setLoading(false);
      setError(true);
      return () => {
        ignore = true;
      };
    }

    setLoading(true);
    setError(false);

    Promise.allSettled([
      recruitmentDetail(recruitmentId),
      recruitmentPage({ current: 1, size: RELATED_RECRUITMENT_COUNT + 1, status: 1 }),
    ])
      .then(([detailRequest, pageRequest]) => {
        if (ignore) return;
        if (detailRequest.status === 'rejected') throw detailRequest.reason;

        const detailResult = detailRequest.value;
        if (detailResult.code !== 0 || !detailResult.data) {
          throw new Error(detailResult.msg || 'Unable to load recruitment detail');
        }

        setRecord(detailResult.data);
        const pageResult = pageRequest.status === 'fulfilled' ? pageRequest.value : null;
        setRelatedRecruitments(
          (pageResult?.code === 0 ? pageResult.data?.records ?? [] : [])
            .filter(item => String(item.id) !== recruitmentId)
            .slice(0, RELATED_RECRUITMENT_COUNT),
        );
      })
      .catch(() => {
        if (ignore) return;
        setRecord(null);
        setRelatedRecruitments([]);
        setError(true);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [recruitmentId, i18n.resolvedLanguage]);

  const safeDuty = useMemo(() => sanitizeRecruitmentHtml(record?.duty), [record?.duty]);
  const safeRequirement = useMemo(
    () => sanitizeRecruitmentHtml(record?.requirement),
    [record?.requirement],
  );
  const targetTypeLabel = record ? getTargetTypeLabel(record, t) : '';

  return (
    <div className="recruit-detail-page">
      <RecruitHero />

      <div className="recruit-detail-subnav">
        <nav className="recruit-detail-breadcrumb" aria-label={t('recruit.detail.breadcrumb')}>
          <Link to="/">{t('common.navigation.home')}</Link>
          <img src={breadcrumbArrow} alt="" />
          <Link to="/recruit">{t('recruit.hero.title')}</Link>
          <img src={breadcrumbArrow} alt="" />
          <span>{record?.title || t('recruit.detail.pageTitle')}</span>
        </nav>
      </div>

      <section className="recruit-detail-content">
        {loading ? (
          <RecruitDetailSkeleton />
        ) : error || !record ? (
          <div className="recruit-detail-empty">{t('recruit.detail.error')}</div>
        ) : (
          <div className="recruit-detail-layout">
            <article className="recruit-detail-main">
              <div className="recruit-detail-main__intro">
                <div className="recruit-detail-main__heading">
                  <h1>{record.title || t('recruit.list.untitled')}</h1>
                  {targetTypeLabel && <span>{targetTypeLabel}</span>}
                </div>

                <h2>{t('recruit.detail.duty')}</h2>
                {safeDuty ? (
                  <div
                    className="recruit-detail-duty"
                    dangerouslySetInnerHTML={{ __html: safeDuty }}
                  />
                ) : (
                  <p className="recruit-detail-no-content">{t('recruit.detail.noDetails')}</p>
                )}
              </div>

              <h2 className="recruit-detail-information-title">
                {t('recruit.detail.information')}
              </h2>
              {safeRequirement ? (
                <div
                  className="recruit-detail-requirement"
                  dangerouslySetInnerHTML={{ __html: safeRequirement }}
                />
              ) : (
                <p className="recruit-detail-no-content recruit-detail-no-content--requirement">
                  {t('recruit.detail.noDetails')}
                </p>
              )}
            </article>

            <aside className="recruit-detail-related" aria-labelledby="related-recruitment-title">
              <h2 id="related-recruitment-title">
                <span aria-hidden="true" />
                {t('recruit.detail.related')}
              </h2>
              {relatedRecruitments.length > 0 ? (
                <div className="recruit-detail-related__list">
                  {relatedRecruitments.map((item, index) => (
                    <RelatedRecruitmentCard
                      key={item.id ?? `${item.title}-${index}`}
                      record={item}
                    />
                  ))}
                </div>
              ) : (
                <p className="recruit-detail-related__empty">{t('recruit.list.empty')}</p>
              )}
            </aside>
          </div>
        )}

        <div className="recruit-detail-actions">
          <a href="mailto:hr@jdxjp.net" className="recruit-detail-back recruit-detail-apply">
            <span className="recruit-detail-back__content">
              {t('recruit.detail.apply')}
              <span className="recruit-detail-back__arrow" aria-hidden="true">
                <img src={backArrow} alt="" />
              </span>
            </span>
          </a>

          <Link to="/recruit" className="recruit-detail-back">
            <span className="recruit-detail-back__content">
              {t('recruit.detail.back')}
              <span className="recruit-detail-back__arrow" aria-hidden="true">
                <img src={backArrow} alt="" />
              </span>
            </span>
          </Link>
        </div>
      </section>

      <div className="recruit-detail-contact">
        <ContactSection overlapPrevious={false} />
      </div>
    </div>
  );
};

export default RecruitDetail;
